import { useState } from "react";
import { Plus, Pencil, Trash2, ArrowLeft, Star, Eye } from "lucide-react";
import { img } from "../../lib/api";
import { VideoWizard, SourceIcon, PROVIDER } from "../../components/admin/VideoWizard";
import { fmtDuration } from "../../lib/video";
import { useList } from "./AdminApp";
import { PageTitle } from "./Layout";
import { useCrud } from "../../lib/adminApi";
import { tr } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Field, I18nInput, ImageUpload, SlugField } from "../../components/admin/Fields";
import SortableList from "../../components/admin/SortableList";
import AllVideos from "../../components/admin/AllVideos";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

function AlbumEditor({ album, onBack }) {
  const [f, setF] = useState(album);
  const { save } = useCrud("video_albums");
  const { data: cats = [] } = useList("video_categories");
  const { data: allVideos = [] } = useList("videos");
  const { data: albums = [] } = useList("video_albums");
  const [wiz, setWiz] = useState(null);
  const vids = useCrud("videos");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const videos = allVideos.filter((v) => v.album_id === f.id);
  const submit = async (e) => { e.preventDefault(); const r = await save({ ...f, slug: f.slug ?? "" }); if (r) setF(r); };
  return (
    <>
      <PageTitle title={f.id ? tr(f.title) || "Albüm" : "Yeni albüm"} action={<Button variant="ghost" onClick={onBack} data-testid="album-back"><ArrowLeft size={15} className="mr-1" />Albümler</Button>} />
      <form onSubmit={submit} className="bg-white border rounded-xl p-5 grid md:grid-cols-2 gap-5" data-testid="album-form">
        <div className="space-y-4">
          <I18nInput label="Albüm adı" value={f.title} onChange={set("title")} testid="album-title" maxLength={120} />
          <I18nInput label="Açıklama" value={f.description} onChange={set("description")} multiline testid="album-description" maxLength={600} />
          <SlugField value={f.slug} onChange={set("slug")} source={f.title?.tr} prefix="/videolar/" testid="album-slug" />
        </div>
        <div className="space-y-4">
          <Field label="Kategori">
            <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={f.category_id || ""} onChange={(e) => set("category_id")(e.target.value || null)} data-testid="album-category">
              <option value="">— Yok —</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{tr(c.name)}</option>)}
            </select>
          </Field>
          <ImageUpload label="Albüm kapağı" value={f.cover} onChange={set("cover")} testid="album-cover" alt={f.title?.tr} />
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.status === "published"} onCheckedChange={(v) => set("status")(v ? "published" : "draft")} data-testid="album-published" />Yayında</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!f.featured} onCheckedChange={set("featured")} data-testid="album-featured" />Anasayfada öne çıkar</label>
          </div>
          <Button data-testid="album-save">Albümü kaydet</Button>
        </div>
      </form>
      {f.id && (
        <div className="mt-8 space-y-4">
          <h2 className="font-semibold">Videolar ({videos.length})</h2>
          <button type="button" onClick={() => setWiz({})} data-testid="open-video-wizard"
            className="w-full rounded-xl border-2 border-dashed border-stone-300 p-6 flex items-center justify-center gap-2 font-medium text-stone-600 hover:border-stone-900 hover:text-stone-900 transition-colors">
            <Plus size={18} />Video ekle
          </button>
          <SortableList items={videos} onReorder={vids.reorder} render={(v) => (
            <div className="flex items-center gap-3" data-testid={`video-row-${v.slug}`}>
              <img src={img(v.thumbnail)} alt="" loading="lazy" decoding="async" className="w-24 aspect-video object-cover rounded bg-stone-100" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{tr(v.title) || "(başlıksız)"}</p>
                <p className="text-xs text-stone-500 flex items-center gap-1.5"><SourceIcon source={v.source} />{PROVIDER[v.source] || "YouTube"}{v.duration ? ` · ${fmtDuration(v.duration)}` : ""}{v.status === "draft" ? " · Taslak" : ""}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setWiz(v)} data-testid={`edit-video-${v.slug}`}><Pencil size={14} /></Button>
              <Button size="icon" variant="ghost" onClick={() => vids.remove(v.id, "Video")} data-testid={`delete-video-${v.slug}`}><Trash2 size={14} className="text-red-500" /></Button>
            </div>
          )} />
          {wiz && <VideoWizard open onClose={() => setWiz(null)} albumId={f.id} albums={albums} edit={wiz.id ? wiz : null} count={videos.length} />}
        </div>
      )}
    </>
  );
}

function Categories() {
  const { data = [] } = useList("video_categories");
  const { save, remove } = useCrud("video_categories");
  const [name, setName] = useState("");
  return (
    <div className="bg-white border rounded-xl p-5 mb-6" data-testid="video-categories-manager">
      <p className="font-semibold mb-3 text-sm">Video kategorileri</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {data.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-2 border rounded-full pl-3 pr-1 py-1 text-sm">{tr(c.name)}
            <button onClick={() => remove(c.id, "Kategori")} className="p-1 text-stone-400 hover:text-red-600" data-testid={`delete-video-cat-${c.slug}`}><Trash2 size={13} /></button></span>
        ))}
      </div>
      <form className="flex gap-2" onSubmit={async (e) => { e.preventDefault(); if (name && (await save({ name: { tr: name } }))) setName(""); }}>
        <Input placeholder="Yeni kategori" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-video-cat-input" />
        <Button data-testid="add-video-cat-button"><Plus size={15} /></Button>
      </form>
    </div>
  );
}

export default function VideosAdmin() {
  const { data = [] } = useList("video_albums");
  const { data: cats = [] } = useList("video_categories");
  const { remove, reorder } = useCrud("video_albums");
  const [editing, setEditing] = useState(null);
  if (editing) return <AlbumEditor album={editing} onBack={() => setEditing(null)} />;
  return (
    <>
      <PageTitle title="Video galerisi" sub="Videoları tek tek ekleyin veya albümlerde toplayın. Tek videoları sonradan istediğiniz albüme taşıyabilirsiniz." />
      <Tabs defaultValue="videos">
        <TabsList className="mb-6">
          <TabsTrigger value="videos" data-testid="videos-tab-all">Tüm videolar</TabsTrigger>
          <TabsTrigger value="albums" data-testid="videos-tab-albums">Albümler</TabsTrigger>
        </TabsList>
        <TabsContent value="videos"><AllVideos /></TabsContent>
        <TabsContent value="albums">
      <div className="flex justify-end mb-4"><Button onClick={() => setEditing({ title: { tr: "" }, description: { tr: "" }, status: "published", featured: false })} data-testid="new-album-button"><Plus size={15} className="mr-1" />Yeni albüm</Button></div>
      <Categories />
      <SortableList items={data} onReorder={reorder} render={(a) => (
        <div className="flex items-center gap-4" data-testid={`album-row-${a.slug}`}>
          {a.cover ? <img src={a.cover.startsWith("/api") ? process.env.REACT_APP_BACKEND_URL + a.cover : a.cover} alt="" loading="lazy" decoding="async" className="w-16 h-12 object-cover rounded" /> : <div className="w-16 h-12 bg-stone-100 rounded" />}
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate flex items-center gap-2">{tr(a.title)} {a.featured && <Star size={13} className="text-amber-500 fill-amber-500" />}</p>
            <p className="text-xs text-stone-400">{tr(cats.find((c) => c.id === a.category_id)?.name) || "Kategorisiz"} · /videolar/{a.slug}</p>
          </div>
          <Badge variant={a.status === "published" ? "default" : "secondary"}>{a.status === "published" ? "Yayında" : "Taslak"}</Badge>
          <Button size="icon" variant="ghost" onClick={() => setEditing(a)} data-testid={`edit-album-${a.slug}`}><Pencil size={14} /></Button>
          <Button size="icon" variant="ghost" onClick={() => remove(a.id, "Albüm ve videoları")} data-testid={`delete-album-${a.slug}`}><Trash2 size={14} className="text-red-500" /></Button>
        </div>
      )} />
        </TabsContent>
      </Tabs>
    </>
  );
}
